import { ImageIcon, ImagesIcon } from 'lucide-react'
import { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AspectRatioInput } from '~/components/AspectRatioInput'
import { Media } from '~/components/Media/Media'
import { MediaManagerContext } from '~/contexts'
import type { MediaManagerContextData } from '~/contexts/MediaManagerContext'
import { useOptionalSeller } from '~/hooks/data/use-seller'
import { useMousePan } from '~/hooks/useMousePan'
import { useScrollObserver } from '~/hooks/useScrollObserver'
import { Button } from '~/ui'
import { cn } from '~/utils/classnames'

import { useCollectionConfiguration } from './collection-configuration-context'

export const CollectionMediaGallery = () => {
  const { t } = useTranslation()
  const seller = useOptionalSeller()
  const { dispatch, collectionAttachments, collection } =
    useCollectionConfiguration()

  const [{ canScroll }, setCanScroll] = useState({
    canScroll: false,
  })
  const [format, setFormat] = useState(
    collection.slideshowMediaFormat || 'square'
  )

  // Sort attachments by disposition from the backend not the frontend
  const sortedAttachments = useMemo(() => {
    return [...(collectionAttachments || [])].sort(
      (a, b) => Number(a.disposition) - Number(b.disposition)
    )
  }, [collectionAttachments])

  const [, setMediaManagerContext] = useContext(MediaManagerContext)
  const { ref } = useMousePan<HTMLDivElement>()

  useScrollObserver(ref, (el) => {
    setCanScroll({
      canScroll: el.scrollWidth > el.clientWidth,
    })
  })

  const mediaManagerOnClick = () => {
    const isNewCollection = !collection.id

    const mediaManagerData: MediaManagerContextData = {
      isOpen: true,
      intent: isNewCollection ? 'return_attachments' : 'upload_attachments',
      maxSelected: 8,
      title: t('components.media_manager.collection_media_title', {
        collection: collection.name,
      }),
      targetType: 'generic',
      targetId: collection.id,
      attachments: collectionAttachments,
    }

    if (isNewCollection) {
      mediaManagerData.onAttachmentsChange = (attachments) => {
        dispatch({
          type: 'patch-collection-attachments',
          payload: attachments,
        })
      }
    } else {
      mediaManagerData.onAttachmentsComplete = (updatedAttachments) => {
        dispatch({
          type: 'patch-collection-attachments',
          payload: updatedAttachments,
        })
      }
    }
    setMediaManagerContext(mediaManagerData)
  }

  const handleChange = (attachmentId: string, key: string) => {
    return (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      dispatch({
        type: 'update-collection-attachment',
        payload: {
          attachmentId,
          updates: { [key]: event.currentTarget.value },
        },
      })
    }
  }

  if (!(collectionAttachments?.length > 0) && seller) {
    return (
      <div className="mx-auto my-4">
        <Button
          type="submit"
          variant="outline"
          className="flex w-fit"
          onClick={mediaManagerOnClick}
        >
          <ImagesIcon />
          <span>{t('action.add_media_gallery')}</span>
        </Button>
      </div>
    )
  }

  return (
    <div className={cn('relative mx-auto mt-10 max-w-full overflow-hidden')}>
      <div
        ref={ref}
        className={cn(
          'hide-scrollbar cursor-grab snap-x snap-mandatory overflow-x-auto active:cursor-grabbing',
          !canScroll && 'cursor-default active:cursor-default'
        )}
      >
        <ul className="mx-3 flex size-max gap-2 @lg:mx-6">
          {sortedAttachments?.map((slide, index) => {
            return (
              <li
                key={`${slide.id}-${index}`}
                className={cn(
                  'relative h-auto w-[90cqw] transition-[aspect-ratio] duration-500 @lg:h-[700px] @lg:w-auto',
                  'no-select snap-center overflow-hidden rounded-md',
                  format === 'portrait' && 'aspect-3/4',
                  format === 'landscape' && 'aspect-4/3',
                  format === 'square' && 'aspect-square'
                )}
              >
                <div className="absolute inset-0 w-full">
                  <Media
                    className="absolute inset-0 h-full w-full object-cover"
                    src={slide.media.src}
                    kind={slide.media.kind}
                    loading="lazy"
                    transformation={{
                      width: 1200,
                      format: 'webp',
                      resizeMode: 'fill',
                    }}
                    sizes={{
                      sm: '100vw',
                      md: '50vw',
                      default: '400px',
                    }}
                  />
                </div>
              </li>
            )
          })}
        </ul>
      </div>

      {seller && (
        <>
          <Button
            variant="over-media"
            className="absolute top-3 right-3 z-10 flex w-fit @lg:right-10"
            onClick={mediaManagerOnClick}
          >
            <ImageIcon />
            <span className="opacity-80">{t('action.manage_media')}</span>
          </Button>

          <AspectRatioInput
            className="absolute right-3 bottom-3 z-10 @lg:right-10"
            value={format}
            onChange={(format) => {
              dispatch({
                type: 'patch',
                payload: { slideshowMediaFormat: format },
              })
              // fetcher.submit({ mediaFormat: format }, { method: 'PATCH' })
              setFormat(format)
            }}
          />
        </>
      )}
    </div>
  )
}
